// Second opinion + consensus — prompt builders, robust JSON parsing and
// reviewer selection. Pure (node:test-able); the App wires them to callAI.

// Preferred independent reviewers, strongest first. A reviewer from a
// different model family than the original catches more of its mistakes.
export const REVIEWER_PREFERENCE = [
  "openai/gpt-5", "openai/gpt-4.1", "google/gemini-2.5-pro", "anthropic/claude-sonnet-4.6",
  "anthropic/claude-sonnet-4.5", "x-ai/grok-4", "deepseek/deepseek-chat", "openai/gpt-4o",
];

export function modelFamily(id) {
  const s = String(id || "").toLowerCase();
  if (/^selfhosted:/.test(s)) return "selfhosted";
  if (/claude|anthropic/.test(s)) return "anthropic";
  if (/gpt|openai|\bo[134]\b|o\d-/.test(s)) return "openai";
  if (/gemini|google/.test(s)) return "google";
  if (/grok|x-ai/.test(s)) return "xai";
  if (/deepseek/.test(s)) return "deepseek";
  if (/llama|meta/.test(s)) return "meta";
  if (/qwen/.test(s)) return "qwen";
  if (/mistral/.test(s)) return "mistral";
  return s.split("/")[0] || "other";
}

// Ordered reviewer candidates: available, different family first, never the
// same model. `available` = ids the org can use (empty = trust preference).
export function pickReviewerCandidates(originalModel, available) {
  const avail = Array.isArray(available) && available.length ? new Set(available) : null;
  const fam = modelFamily(originalModel);
  const pool = REVIEWER_PREFERENCE.filter((id) => id !== originalModel && (!avail || avail.has(id)));
  const other = pool.filter((id) => modelFamily(id) !== fam);
  const same = pool.filter((id) => modelFamily(id) === fam);
  return [...other, ...same].slice(0, 4);
}

export function buildReviewPrompt(question, answer) {
  return `You are an independent expert reviewer giving a second opinion on another AI assistant's answer. Think it through on your own first, then compare.

# The user's question
${question || "(not available — judge the answer on its own terms)"}

# The answer under review
${answer}

# Respond with ONLY a JSON object, no prose before or after:
{
  "verdict": "agree" | "partially_agree" | "disagree",
  "confidence": <0-100, how confident you are in your verdict>,
  "summary": "<one or two sentences a busy reader can trust>",
  "issues": [ { "claim": "<quote or paraphrase from the answer>", "problem": "<what is wrong, missing or risky>", "correction": "<what is actually right>" } ],
  "strengths": ["<what the answer got right>"],
  "own_answer": "<your own concise answer to the question, in Markdown>"
}
Use "agree" only if you found no material errors or omissions. List at most 6 issues, most important first. If unsure about a fact, say so in the issue instead of guessing.`;
}

export function buildConsensusPrompt(question, answers) {
  const blocks = answers.map((a, i) => `## Answer ${i + 1} — ${a.label}\n${a.content || "(empty)"}`).join("\n\n---\n\n");
  return `Several AI models answered the same question. Map where they agree and where they genuinely disagree. Ignore differences in style or length.

# Question
${question || "(not available)"}

${blocks}

# Respond with ONLY a JSON object:
{
  "consensus": "high" | "mixed" | "low",
  "agreed": ["<a substantive point all answers support>"],
  "disputed": [ { "point": "<topic of disagreement>", "positions": [ { "answer": <answer number>, "says": "<that answer's position>" } ], "likely_correct": "<which position is better supported and why, or 'unclear'>" } ],
  "unique": [ { "answer": <answer number>, "insight": "<useful point only this answer made>" } ],
  "recommendation": "<one or two sentences: what the reader should take away or verify>"
}`;
}

// Pull the first JSON object out of a model reply (handles ```json fences
// and stray prose). Returns null when nothing parseable is found.
export function extractJson(text) {
  const s = String(text || "");
  const fenced = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidates = [fenced && fenced[1], s];
  for (const c of candidates) {
    if (!c) continue;
    const start = c.indexOf("{");
    if (start < 0) continue;
    let depth = 0, inStr = false, esc = false;
    for (let i = start; i < c.length; i++) {
      const ch = c[i];
      if (inStr) { if (esc) esc = false; else if (ch === "\\") esc = true; else if (ch === '"') inStr = false; continue; }
      if (ch === '"') inStr = true;
      else if (ch === "{") depth++;
      else if (ch === "}" && --depth === 0) {
        try { return JSON.parse(c.slice(start, i + 1)); } catch { break; }
      }
    }
  }
  return null;
}

const VERDICTS = new Set(["agree", "partially_agree", "disagree"]);
const str = (v, n) => (typeof v === "string" ? v.trim().slice(0, n) : "");

export function normalizeReview(raw) {
  const j = extractJson(raw);
  if (!j || typeof j !== "object") return { ok: false, verdict: null, summary: str(raw, 1200) || "The reviewer did not return a usable verdict.", issues: [], strengths: [], ownAnswer: "", confidence: null };
  const v = String(j.verdict || "").toLowerCase().replace(/\s+/g, "_");
  return {
    ok: VERDICTS.has(v),
    verdict: VERDICTS.has(v) ? v : null,
    confidence: Number.isFinite(Number(j.confidence)) ? Math.max(0, Math.min(100, Math.round(Number(j.confidence)))) : null,
    summary: str(j.summary, 600),
    issues: (Array.isArray(j.issues) ? j.issues : []).slice(0, 6).map((x) => ({ claim: str(x && x.claim, 300), problem: str(x && x.problem, 400), correction: str(x && x.correction, 400) })).filter((x) => x.problem || x.claim),
    strengths: (Array.isArray(j.strengths) ? j.strengths : []).slice(0, 5).map((x) => str(x, 200)).filter(Boolean),
    ownAnswer: str(j.own_answer, 8000),
  };
}

const LEVELS = new Set(["high", "mixed", "low"]);
export function normalizeConsensus(raw, count) {
  const j = extractJson(raw);
  if (!j || typeof j !== "object") return { ok: false, consensus: null, agreed: [], disputed: [], unique: [], recommendation: str(raw, 800) };
  const n = (x) => { const k = Math.round(Number(x)); return k >= 1 && k <= count ? k : null; };
  return {
    ok: LEVELS.has(String(j.consensus).toLowerCase()),
    consensus: LEVELS.has(String(j.consensus).toLowerCase()) ? String(j.consensus).toLowerCase() : null,
    agreed: (Array.isArray(j.agreed) ? j.agreed : []).slice(0, 10).map((x) => str(x, 300)).filter(Boolean),
    disputed: (Array.isArray(j.disputed) ? j.disputed : []).slice(0, 8).map((d) => ({
      point: str(d && d.point, 200),
      positions: (Array.isArray(d && d.positions) ? d.positions : []).map((p) => ({ answer: n(p && p.answer), says: str(p && p.says, 300) })).filter((p) => p.answer && p.says),
      likelyCorrect: str(d && d.likely_correct, 300),
    })).filter((d) => d.point),
    unique: (Array.isArray(j.unique) ? j.unique : []).slice(0, 8).map((u) => ({ answer: n(u && u.answer), insight: str(u && u.insight, 300) })).filter((u) => u.answer && u.insight),
    recommendation: str(j.recommendation, 500),
  };
}

// The user question that produced message index `idx`.
export function questionBefore(messages, idx) {
  for (let i = idx - 1; i >= 0; i--) {
    const m = messages[i];
    if (m && m.role === "user") return typeof m.content === "string" ? m.content : String(m.content || "");
  }
  return "";
}
